import React, {createElement, forwardRef} from 'react';
import Icon, {Sizes} from '@pb/kit-component-icon';
import Loading, {LoadingHolder, Types as LoadingTypes} from '@pb/kit-component-loading';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import type {ComponentProps} from 'react';
import type {IconSize, IconType, IconProps} from '@pb/kit-component-icon';

import styles from './icon-button.component.module.styl';
import {FlatButton} from '@pb/kit-component-button';


const noop = () => undefined;
const classNames = withCssModule(styles);
const classes = hashedClasses(styles);


type NativeButtonProps = Omit<ComponentProps<'button'>, 'ref'>;

export interface BasicIconButtonProps extends NativeButtonProps {
    /**
     * Element type
     */
    as?: 'button' | 'a',
    /**
     * Inline displaying
     * */
    inline?: boolean,
    /**
     * Will render Link if `handler` is string type
     * If `handler` is function type will be called as onClick callback, nothing happens if button is disabled
     * @deprecated It will be removed in 6 months 09-15-2025. link functionality is deprecated, use onClick instead
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handler?: string | ((name: any | undefined, event: React.MouseEvent<HTMLButtonElement>) => unknown),
    /**
     * Link type - internal/external
     * @deprecated It will be removed in 6 months 09-15-2025. link functionality is deprecated, use onClick instead
     * */
    internal?: boolean,
    /**
     * Name of icon button
     * @deprecated It will be removed in 6 months 09-15-2025. name and handler are deprecated, use onClick instead
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    name?: any,
    /**
     * Custom IconButton className
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mix?: string,
    /**
     * Replace icon with loader
     */
    loader?: boolean,
    /**
     * Specifies the URL the hyperlink points to. Use with as='a'
     * Temporary solution until Flow is removed
     */
    href?: string,
    /**
     * Link target. Use with as='a'
     * Temporary solution until Flow is removed
     */
    target?: ComponentProps<'a'>['target'],
    /**
     * Icon properties, iconType and iconSize are included in this object
     */
    iconProps?: IconProps,
    /**
     * Icon for button taken from collection. Icon and iconSize should be removed
     * @deprecated It will be removed in 6 months 09-15-2025. use iconProps instead
     * */
    icon?: IconType,
    /**
     * Icon size for button. Icon and iconSize should be removed
     * @deprecated It will be removed in 6 months 09-15-2025. use iconProps instead
     * */
    iconSize?: IconSize,
    /**
     * ref to the element
     */
    ref?: React.Ref<HTMLButtonElement>,
}

export const BasicIconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, BasicIconButtonProps>(
    function BasicIconButton(props, ref) {
        const {
            as = 'button',
            internal = true,
            handler = noop,
            loader = false,
            mix = '',
            className = '',
            inline = false,
            iconProps,
            icon,
            iconSize = Sizes.Size_14,
            type = 'button',
            ...restProps
        } = props;

        const iconButtonClasses = classNames(mix, className, 'icon-button', {
            'icon-button_inline': inline,
            'icon-button_with-loader': loader,
        });

        let buttonProps = {};
        if (as === 'button') {
            const {disabled = false, onClick} = restProps;

            buttonProps = {
                type,
                disabled,
                'onClick': (e: React.MouseEvent<HTMLButtonElement>) => {
                    if (!disabled && typeof handler === 'function') {
                        handler(props.name, e);
                    }
                    onClick?.(e);
                },
                ref,
                'className': iconButtonClasses,
                'aria-busy': loader ? true : undefined,
            };
        }

        const children = (
            <>
                <Icon
                    size={iconSize}
                    {...iconProps}
                    type={iconProps?.type || icon || ''}
                />
                {loader ? (
                    <LoadingHolder>
                        <Loading type={LoadingTypes.ICON} aria-hidden="true"/>
                    </LoadingHolder>
                ) : null}
            </>
        );

        if (typeof handler === 'string') {
            return (
                <FlatButton
                    as="a"
                    href={handler}
                    target={internal ? undefined : '_blank'}
                    {...buttonProps}>
                    {children}
                </FlatButton>
            );
        }

        return createElement(
            as,
            {
                ...restProps,
                ...buttonProps,
                className: iconButtonClasses,
            },
            as === 'button'
                ? <span className={classes['icon-button__hole']}>{children}</span>
                : children,
        );
    },
);

BasicIconButton.displayName = 'BasicIconButton';
